/**
 * Utility functions for cart operations
 */

export interface CartItem {
  product_id: string;
  name: string;
  vendor_id: number;
  vendor_name: string;
  prod_img: string | null;
  isEggless: boolean;
  selectedVariant: Record<string, unknown> | null;
  count: number;
  customizations: (Record<string, unknown> | number | string)[];
  specialInstructions: string;
  selectedMessage: string;
  selectedFlavor: Record<string, unknown> | null;
  final_price_calculated?: string | number;
  note?: string;
  customizedName?: string;
  availableWeightVariantsCount?: number;
  uniqueId: string;
  vendor_details?: Record<string, unknown>;
}

export interface CheckoutPayload {
  note: string;
  lines: Array<{
    product: number;
    quantity: number;
    is_eggless: boolean;
    weight_variant: number | null;
    customization_options: number[];
    key_note: string | null;
    customized_name: string | null;
  }>;
  vendor: number;
}

/**
 * Transforms localStorage cart items into the required checkout format
 * @param cartItems - Array of cart items from localStorage
 * @param note - Optional note for the order
 * @returns Formatted checkout payload
 */
export const formatCartForCheckout = (
  cartItems: CartItem[],
  note: string = "",
): CheckoutPayload => {
  if (!cartItems || cartItems.length === 0) {
    throw new Error("No cart items to format");
  }

  // Get vendor ID from the first item (all items should have the same vendor)
  const vendorId = cartItems[0]?.vendor_id;
  if (!vendorId) {
    throw new Error("Vendor ID not found in cart items");
  }

  const lines = cartItems.map((item) => {
    // Extract customization option IDs
    const customizationOptions =
      item.customizations?.map((customization: Record<string, unknown> | number | string) =>
        typeof customization === "object" && customization !== null ? (customization as Record<string, unknown>).id : customization,
      ) || [];

    return {
      product: parseInt(item.product_id),
      quantity: item.count || 1,
      is_eggless: item.isEggless || false,
      weight_variant: (item.selectedVariant?.id as number) || null,
      customization_options: customizationOptions as number[],
      key_note: item.note || null,
      customized_name: item.customizedName || null,
    };
  });

  return {
    note,
    lines,
    vendor: vendorId,
  };
};

/**
 * Loads cart items from localStorage and formats them for checkout
 * @param storageKey - localStorage key for cart items
 * @param note - Optional note for the order
 * @returns Formatted checkout payload
 */
export const loadAndFormatCart = (
  storageKey: string = "toofi_cart_items",
  note: string = "",
): CheckoutPayload => {
  try {
    const storedItems = localStorage.getItem(storageKey);
    if (!storedItems) {
      throw new Error("No cart items found in localStorage");
    }

    const cartItems: CartItem[] = JSON.parse(storedItems);
    return formatCartForCheckout(cartItems, note);
  } catch (error) {
    console.error("Failed to load and format cart:", error);
    throw error;
  }
};

/**
 * Validates cart items before formatting
 * @param cartItems - Array of cart items
 * @returns true if valid, throws error if invalid
 */
export const validateCartItems = (cartItems: CartItem[]): boolean => {
  if (!cartItems || cartItems.length === 0) {
    throw new Error("Cart is empty");
  }

  // Check if all items have the same vendor
  const vendorId = cartItems[0]?.vendor_id;
  const hasSameVendor = cartItems.every((item) => item.vendor_id === vendorId);

  if (!hasSameVendor) {
    throw new Error("All cart items must be from the same vendor");
  }

  // Validate each item has required fields
  cartItems.forEach((item, index) => {
    if (!item.product_id) {
      throw new Error(`Item at index ${index} is missing product_id`);
    }
    if (!item.vendor_id) {
      throw new Error(`Item at index ${index} is missing vendor_id`);
    }
  });

  return true;
};

export const getCartItemUniqueId = (item: {
  product_id: number | string;
  isEggless: boolean;
  selectedVariant: Record<string, unknown> | null;
  customizations: (Record<string, unknown> | number | string)[];
  specialInstructions: string;
  note?: string;
  customizedName?: string;
}): string => {
  const sortedCustomizations =
    item?.customizations?.length > 0
      ? item.customizations
          .toSorted((a: Record<string, unknown> | number | string, b: Record<string, unknown> | number | string) => {
            const aId = typeof a === "object" && a !== null ? (a as Record<string, unknown>).id : a;
            const bId = typeof b === "object" && b !== null ? (b as Record<string, unknown>).id : b;
            return (aId as number) - (bId as number);
          })
          .map((customization: Record<string, unknown> | number | string) => typeof customization === "object" && customization !== null ? (customization as Record<string, unknown>).id : customization)
      : [];
  // Normalize weight_variant: treat 0 as null since 0 is not a valid weight variant ID
  const normalizedWeightVariant = item?.selectedVariant?.id
    ? item.selectedVariant.id
    : null;
  return `${item.product_id}_${item.isEggless ? "eggless" : "regular"}_${normalizedWeightVariant}_${sortedCustomizations.join(",")}_${item.specialInstructions || ""}_${item.note || ""}_${item.customizedName || ""}`;
};

export const mapProductsToLines = (
  products: CartItem[],
): Array<{
  product: number;
  quantity: number;
  is_eggless: boolean;
  weight_variant: number | null;
  customization_options: number[];
  key_note: string | null;
  customized_name: string | null;
}> =>
  products?.map((product) => ({
    product: Number(product.product_id),
    quantity: product.count,
    is_eggless: product.isEggless,
    weight_variant: (product.selectedVariant?.id as number) || null,
    customization_options:
      (product.customizations?.map((customization: Record<string, unknown> | number | string) => typeof customization === "object" && customization !== null ? (customization as Record<string, unknown>).id as number : customization as number) ||
      []),
    key_note: product.note || null,
    customized_name: product.customizedName || null,
  }));

export interface CartProductData {
  id: string | number;
  name: string;
  vendor: number;
  vendor_details?: { business_name?: string };
  product_urls?: string[];
  weight_variants?: Record<string, unknown>[];
  primary_flavor_details?: { name: string; id: string | number };
  secondary_flavor_details?: { name: string; id: string | number };
  final_price?: string | number;
  gst_rate?: number;
  slug?: string;
  in_stock?: boolean;
  visible_in_listings?: boolean;
}

export interface CheckoutLineData {
  product: CartProductData;
  quantity: number;
  is_eggless: boolean;
  weight_variant?: { flavour?: { name: string; id: string | number } } | null;
  customization_options?: { customization_info?: { label?: string } }[];
  special_instructions?: string;
  key_note?: string;
  customized_name?: string;
  vendor_name?: string;
}

export const mapLinesToProducts = (lines: CheckoutLineData[]) => {
  return lines.map((line) => {
    const product = line.product;

    // Map customizations with label from customization_info
    const customizations = (line.customization_options || []).map(
      (customization: Record<string, unknown>) => ({
        ...customization,
        label: (customization.customization_info as { label?: string })?.label || "",
      }),
    );

    const availableWeightVariantsCount = product.weight_variants?.length || 0;

    // Determine selected flavor (primary or secondary)
    const selectedFlavor = line.weight_variant?.flavour
      ? {
          name: line.weight_variant.flavour.name,
          id: line.weight_variant.flavour.id,
        }
      : product.primary_flavor_details
        ? {
            name: product.primary_flavor_details.name,
            id: product.primary_flavor_details.id,
          }
        : product.secondary_flavor_details
          ? {
              name: product.secondary_flavor_details.name,
              id: product.secondary_flavor_details.id,
            }
          : null;

    const finalProduct = {
      product_id: Number(product.id),
      name: product.name,
      vendor_id: product.vendor,
      vendor_name:
        line.vendor_name || product.vendor_details?.business_name || "",
      prod_img: product.product_urls?.[0] || null,
      isEggless: line.is_eggless || false,
      selectedVariant: line.weight_variant as Record<string, unknown> | null,
      count: line.quantity || 1,
      customizations: customizations,
      specialInstructions: line.special_instructions || "",
      selectedMessage: line.key_note || "",
      customizedName: line.customized_name || "",
      selectedFlavor: selectedFlavor,
      availableWeightVariantsCount: availableWeightVariantsCount,
      final_price_calculated: Number(product.final_price) * (line.quantity || 1),
      vendor_details: product.vendor_details as Record<string, unknown> | undefined,
      gst_rate: product.gst_rate || 0,
      slug: product.slug || "",
      in_stock: product.in_stock || false,
      visible_in_listings: product?.visible_in_listings || false,
    };

    return {
      ...finalProduct,
      uniqueId: getCartItemUniqueId(finalProduct),
    };
  });
};

export const mergeAndMapLinesToProducts = (products: CartItem[], lines: CheckoutLineData[]) => {
  const mappedLines = lines.length > 0 ? mapLinesToProducts(lines) : [];
  return products.length > 0
    ? products.map((product) => {
        const line = mappedLines.find(
          (line: { uniqueId?: string; count?: number; final_price_calculated?: string | number }) => line.uniqueId === product.uniqueId,
        );
        return {
          ...product,
          count: (line?.count || 0) + product.count,
          final_price_calculated: (Number(product?.final_price_calculated) || 0) + (Number(line?.final_price_calculated) || 0),
        };
      })
    : mappedLines;
};
